import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { quotationsApi } from "@/services/salesDocumentsApi";
import type {
  CreateQuotationRequest,
  ConvertQuotationToSalesOrderRequest,
} from "@/types/salesDocuments";

// Query key factory
const quotationKeys = {
  all: ["quotations"] as const,
  lists: () => [...quotationKeys.all, "list"] as const,
  list: (filters?: any) => [...quotationKeys.lists(), filters] as const,
  details: () => [...quotationKeys.all, "detail"] as const,
  detail: (id: string) => [...quotationKeys.details(), id] as const,
};

// Get all quotations
export const useQuotations = (filters?: {
  search?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}) => {
  return useQuery({
    queryKey: quotationKeys.list(filters),
    queryFn: () => quotationsApi.getAll(filters),
  });
};

// Get single quotation
export const useQuotation = (id: string | undefined) => {
  return useQuery({
    queryKey: quotationKeys.detail(id || ""),
    queryFn: () => quotationsApi.getById(id!),
    enabled: !!id,
  });
};

// Create quotation
export const useCreateQuotation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateQuotationRequest) => quotationsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: quotationKeys.lists() });
    },
  });
};

// Update quotation
export const useUpdateQuotation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateQuotationRequest> }) =>
      quotationsApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: quotationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: quotationKeys.detail(variables.id) });
    },
  });
};

// Delete quotation
export const useDeleteQuotation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => quotationsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: quotationKeys.lists() });
    },
  });
};

// Convert quotation to sales order
export const useConvertQuotationToSalesOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ConvertQuotationToSalesOrderRequest) =>
      quotationsApi.convertToSalesOrder(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: quotationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: quotationKeys.detail(variables.quotationId) });
      queryClient.invalidateQueries({ queryKey: ["salesOrders"] });
    },
  });
};

// Update quotation status
export const useUpdateQuotationStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      quotationsApi.updateStatus(id, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: quotationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: quotationKeys.detail(variables.id) });
    },
  });
};

// Clone quotation
export const useCloneQuotation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => quotationsApi.clone(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: quotationKeys.lists() });
    },
  });
};

// Send quotation emails to buyers
export const useSendQuotationEmail = () => {
  return useMutation({
    mutationFn: (ids: string[]) => quotationsApi.sendEmail(ids),
  });
};

// Hook to use all quotation operations
export const useQuotationOperations = () => {
  const createQuotation = useCreateQuotation();
  const updateQuotation = useUpdateQuotation();
  const deleteQuotation = useDeleteQuotation();
  const convertToSalesOrder = useConvertQuotationToSalesOrder();
  const updateStatus = useUpdateQuotationStatus();
  const cloneQuotation = useCloneQuotation();

  return {
    createQuotation,
    updateQuotation,
    deleteQuotation,
    convertToSalesOrder,
    updateStatus,
    cloneQuotation,
    isLoading:
      createQuotation.isPending ||
      updateQuotation.isPending ||
      deleteQuotation.isPending ||
      convertToSalesOrder.isPending ||
      updateStatus.isPending ||
      cloneQuotation.isPending,
  };
};
